import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Tabs,
  Empty,
  Tag,
  Table,
  Card
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  KeyOutlined,
  LockOutlined,
  UnlockOutlined,
  StopOutlined,
  HistoryOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  MailOutlined,
  PhoneOutlined,
  ReloadOutlined,
  QrcodeOutlined
} from '@ant-design/icons';
import endpoints from '../../api/endpoints';
import { useQuery } from '../../hooks/useApi';
import { useAppNotifications } from '../../utils/notify';
import {
  sanitizeText,
  sanitizeEmail,
  sanitizeMobile
} from '../../utils/sanitize';
import { isEmail, isMobileIN, passwordStrength, digitsOnly } from '../../utils/validators';
import { formatDateTime } from '../../utils/format';
import StatusBadge from '../../components/StatusBadge';
import LoadingBlock from '../../components/LoadingBlock';
import EmptyState from '../../components/EmptyState';
import UserIpAccessSection from './UserIpAccessSection';
import UserMfaSection from './UserMfaSection';
import MasterMfaSection from './MasterMfaSection';
import RecoveryCodesSection from './RecoveryCodesSection';
import useAuth from '../../hooks/useAuth';

// Stage 5L: inline user-details panel that replaces UserProfileDrawer.
// Same content (header + Profile / Access & Security / Activity tabs)
// rendered as a regular Card inside the Users page instead of inside an
// AntD Drawer overlay. Sub-components (EditProfileForm, reset/change
// password panels, ActivitySection, UserIpAccessSection) are reused.

function initialsOf(name, email) {
  const src = (name || email || 'U').trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function resolveStatus(row) {
  if (!row) return 'active';
  if (row.status) return row.status;
  if (row.isMaster) return 'master';
  return row.isActive ? 'active' : 'surrendered';
}

// ---- profile edit form ----
function EditProfileForm({ user, selfMode, onSaved, onCancel, highlight }) {
  const { notifyError, notifySuccess } = useAppNotifications();
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const wrapperRef = useRef(null);

  // On mount (the form renders only when editing=true), bring the form
  // into view and focus the first input. requestAnimationFrame waits
  // one paint so the tab content is laid out before we measure.
  useEffect(() => {
    requestAnimationFrame(() => {
      const node = wrapperRef.current;
      if (!node) return;
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const firstInput = node.querySelector('input, textarea');
      firstInput?.focus?.();
    });
  }, []);

  async function submit(values) {
    setBusy(true);
    try {
      const body = {
        displayName: sanitizeText(values.displayName, {
          collapseWhitespace: true,
          maxLength: 120
        }),
        profileEmail: values.profileEmail
          ? sanitizeEmail(values.profileEmail)
          : '',
        profileMobile: values.profileMobile
          ? sanitizeMobile(values.profileMobile)
          : ''
      };
      const updated = selfMode
        ? await endpoints.users.updateMyProfile(body)
        : await endpoints.users.updateProfile(user.id, body);
      notifySuccess('Profile saved', `${updated.displayName} updated.`);
      onSaved(updated);
    } catch (err) {
      notifyError(err, { title: 'Could not save profile' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      ref={wrapperRef}
      tabIndex={-1}
      className={`rounded-xl transition-shadow ${
        highlight
          ? 'ring-2 ring-brand-300 shadow-[0_0_0_4px_rgba(24,119,242,0.08)] p-3 -m-3'
          : ''
      }`}
    >
    <Form
      form={form}
      layout="vertical"
      disabled={busy}
      size="small"
      onFinish={submit}
      initialValues={{
        displayName: user.displayName || user.fullName || '',
        profileEmail: user.profileEmail || '',
        profileMobile: user.profileMobile || ''
      }}
    >
      <p className="text-xs text-ink-500 m-0 mb-2">
        Profile email and mobile are used as contact details. Login email
        will not change.
      </p>
      <Form.Item
        label={<span className="text-sm font-medium text-ink-700">Display name</span>}
        name="displayName"
        rules={[
          { required: true, message: 'display name is required' },
          { min: 2, max: 120, message: '2–120 characters' }
        ]}
      >
        <Input prefix={<UserOutlined className="text-ink-400" />} />
      </Form.Item>
      <Form.Item
        label={<span className="text-sm font-medium text-ink-700">Mobile</span>}
        name="profileMobile"
        getValueFromEvent={digitsOnly(10)}
        rules={[
          {
            validator: (_r, v) =>
              !v || isMobileIN(String(v))
                ? Promise.resolve()
                : Promise.reject(new Error('10-digit Indian mobile starting with 6-9'))
          }
        ]}
      >
        <Input
          prefix={<PhoneOutlined className="text-ink-400" />}
          placeholder="9876543210"
          maxLength={10}
          inputMode="numeric"
          autoComplete="tel"
        />
      </Form.Item>
      <Form.Item
        label={<span className="text-sm font-medium text-ink-700">Email</span>}
        name="profileEmail"
        rules={[
          {
            validator: (_r, v) =>
              !v || isEmail(String(v))
                ? Promise.resolve()
                : Promise.reject(new Error('enter a valid email'))
          }
        ]}
      >
        <Input prefix={<MailOutlined className="text-ink-400" />} placeholder="anita@example.com" />
      </Form.Item>
      <div className="flex items-center justify-end gap-2">
        <Button size="small" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button size="small" type="primary" htmlType="submit" loading={busy}>
          Save profile
        </Button>
      </div>
    </Form>
    </div>
  );
}

// ---- admin reset-password panel ----
// Mounted by parent only when open=true (conditional render), so we
// don't need an `open` prop or the resetFields-on-close effect — the
// component remounts fresh each time the action is triggered.
function AdminResetPasswordPanel({ user, onClose, onDone, highlight }) {
  const { notifyError, notifySuccess } = useAppNotifications();
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const wrapperRef = useRef(null);

  // Scroll into view + focus first input after the parent flips the
  // tab to "security" and mounts this panel. requestAnimationFrame
  // waits one paint so layout has caught up.
  useEffect(() => {
    requestAnimationFrame(() => {
      const node = wrapperRef.current;
      if (!node) return;
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const firstInput = node.querySelector('input');
      firstInput?.focus?.();
    });
  }, []);

  async function submit(values) {
    setBusy(true);
    try {
      await endpoints.users.adminResetPassword(user.id, {
        newPassword: values.newPassword,
        forceLogout: values.forceLogout !== false
      });
      notifySuccess(
        'Password reset',
        `${user.displayName || user.email} can sign in with the new password.`
      );
      form.resetFields();
      onDone();
    } catch (err) {
      notifyError(err, { title: 'Could not reset password' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      ref={wrapperRef}
      tabIndex={-1}
      className={`rounded-xl border border-rose-200 bg-rose-50/40 p-3 mt-2 mb-3 transition-shadow ${
        highlight ? 'ring-2 ring-rose-300 shadow-[0_0_0_4px_rgba(244,63,94,0.10)]' : ''
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <KeyOutlined className="text-rose-500" />
        <h3 className="text-sm font-semibold text-ink-900 m-0">Reset user password</h3>
      </div>
      <p className="text-xs text-ink-700 m-0 mb-2">
        The user will need to use the new password the next time they log in.
      </p>
      <Form
        form={form}
        layout="vertical"
        disabled={busy}
        size="small"
        onFinish={submit}
        initialValues={{ forceLogout: true }}
      >
        <Form.Item
          label={<span className="text-sm font-medium text-ink-700">New password</span>}
          name="newPassword"
          rules={[
            { required: true, message: 'password is required' },
            {
              validator: (_r, v) => {
                const s = passwordStrength(v || '');
                return s.ok
                  ? Promise.resolve()
                  : Promise.reject(new Error('needs ' + s.reasons.join(', ')));
              }
            }
          ]}
          extra={
            <span className="text-xs text-ink-500">
              At least 12 chars with upper, lower, digit, symbol.
            </span>
          }
        >
          <Input.Password prefix={<LockOutlined className="text-ink-400" />} />
        </Form.Item>
        <Form.Item
          label={<span className="text-sm font-medium text-ink-700">Confirm password</span>}
          name="confirm"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'please confirm the password' },
            ({ getFieldValue }) => ({
              validator: (_r, v) =>
                !v || v === getFieldValue('newPassword')
                  ? Promise.resolve()
                  : Promise.reject(new Error('passwords do not match'))
            })
          ]}
        >
          <Input.Password prefix={<LockOutlined className="text-ink-400" />} />
        </Form.Item>
        <Form.Item
          name="forceLogout"
          valuePropName="checked"
          className="!mb-3"
        >
          <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
            <input
              type="checkbox"
              defaultChecked
              onChange={(e) => form.setFieldValue('forceLogout', e.target.checked)}
            />
            Sign out this user from other sessions
          </label>
        </Form.Item>
        <div className="flex items-center justify-end gap-2">
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="primary" danger htmlType="submit" loading={busy}>
            Reset password
          </Button>
        </div>
      </Form>
    </div>
  );
}

// ---- self change-password panel ----
// Mounted by parent only when changeOpen=true (conditional render), so
// we don't need an `open` prop or the resetFields-on-close effect.
function ChangeMyPasswordPanel({ onClose, onDone, highlight }) {
  const { notifyError, notifySuccess } = useAppNotifications();
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      const node = wrapperRef.current;
      if (!node) return;
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const firstInput = node.querySelector('input');
      firstInput?.focus?.();
    });
  }, []);

  async function submit(values) {
    setBusy(true);
    try {
      await endpoints.users.selfChangePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });
      notifySuccess('Password changed', 'Other sessions have been signed out.');
      form.resetFields();
      onDone();
    } catch (err) {
      notifyError(err, { title: 'Could not change password' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      ref={wrapperRef}
      tabIndex={-1}
      className={`rounded-xl border border-brand-200 bg-brand-50/40 p-3 mt-2 mb-3 transition-shadow ${
        highlight ? 'ring-2 ring-brand-300 shadow-[0_0_0_4px_rgba(24,119,242,0.10)]' : ''
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <KeyOutlined className="text-brand-500" />
        <h3 className="text-sm font-semibold text-ink-900 m-0">Change my password</h3>
      </div>
      <Form
        form={form}
        layout="vertical"
        disabled={busy}
        size="small"
        onFinish={submit}
      >
        <Form.Item
          label={<span className="text-sm font-medium text-ink-700">Current password</span>}
          name="currentPassword"
          rules={[{ required: true, message: 'current password is required' }]}
        >
          <Input.Password prefix={<LockOutlined className="text-ink-400" />} />
        </Form.Item>
        <Form.Item
          label={<span className="text-sm font-medium text-ink-700">New password</span>}
          name="newPassword"
          rules={[
            { required: true, message: 'new password is required' },
            {
              validator: (_r, v) => {
                const s = passwordStrength(v || '');
                return s.ok
                  ? Promise.resolve()
                  : Promise.reject(new Error('needs ' + s.reasons.join(', ')));
              }
            },
            ({ getFieldValue }) => ({
              validator: (_r, v) =>
                !v || v !== getFieldValue('currentPassword')
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error('new password must differ from current password')
                    )
            })
          ]}
          extra={
            <span className="text-xs text-ink-500">
              At least 12 chars with upper, lower, digit, symbol.
            </span>
          }
        >
          <Input.Password prefix={<LockOutlined className="text-ink-400" />} />
        </Form.Item>
        <Form.Item
          label={<span className="text-sm font-medium text-ink-700">Confirm new password</span>}
          name="confirm"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'please confirm the new password' },
            ({ getFieldValue }) => ({
              validator: (_r, v) =>
                !v || v === getFieldValue('newPassword')
                  ? Promise.resolve()
                  : Promise.reject(new Error('passwords do not match'))
            })
          ]}
        >
          <Input.Password prefix={<LockOutlined className="text-ink-400" />} />
        </Form.Item>
        <div className="flex items-center justify-end gap-2">
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={busy}>
            Change password
          </Button>
        </div>
      </Form>
    </div>
  );
}

function ActivitySection({ loading, items, error, onRefresh }) {
  const list = useMemo(() => items || [], [items]);

  if (loading) return <LoadingBlock height={200} />;
  if (error) {
    return (
      <div className="py-8">
        <Empty description="Could not load activity" />
        <div className="flex justify-center mt-2">
          <Button icon={<ReloadOutlined />} onClick={onRefresh}>
            Retry
          </Button>
        </div>
      </div>
    );
  }
  if (list.length === 0) {
    return (
      <EmptyState
        icon={<HistoryOutlined />}
        title="No activity yet"
        description="User actions will appear here as they happen."
      />
    );
  }

  function actorLabel(evt) {
    if (evt.actorDisplayName) {
      return `${evt.actorDisplayName}${evt.actorIsMaster ? ' (master)' : ''}`;
    }
    if (evt.actorType === 'system') return 'system';
    return 'unknown';
  }

  return (
    <div className="pb-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[11px] uppercase tracking-[0.14em] text-ink-500 font-semibold m-0">
          Recent activity (last 10)
        </h3>
        <Button
          size="small"
          type="text"
          icon={<ReloadOutlined />}
          onClick={onRefresh}
        >
          Refresh
        </Button>
      </div>
      {/* Stage 5L: compact spreadsheet-style table — three columns,
          tight cell padding, monospace timestamp, no pagination
          (always shows the latest 10). */}
      <div className="rounded-lg border border-surface-200 overflow-hidden">
        <Table
          size="small"
          rowKey="id"
          dataSource={list}
          pagination={false}
          tableLayout="fixed"
          className="user-activity-table"
          columns={[
            {
              title: 'Action',
              dataIndex: 'label',
              ellipsis: true,
              render: (v) => (
                <span className="text-[12.5px] font-medium text-ink-900">
                  {v}
                </span>
              )
            },
            {
              title: 'By',
              key: 'actor',
              width: 160,
              ellipsis: true,
              render: (_v, evt) => (
                <span className="text-[12px] text-ink-700">
                  {actorLabel(evt)}
                </span>
              )
            },
            {
              title: 'When',
              dataIndex: 'createdAt',
              key: 'createdAt',
              width: 160,
              render: (v) => (
                <span className="text-[12px] font-mono text-ink-600 whitespace-nowrap">
                  {formatDateTime(v)}
                </span>
              )
            }
          ]}
        />
      </div>
    </div>
  );
}

// ---- main inline panel ----
//
// Props:
//   user            the user record from /api/users (or /me/profile)
//   currentUserId   id of the logged-in user — drives self-mode
//   onChanged       called after any successful action so parent refetches
//   onLock/onUnlock/onSurrender   parent-provided lifecycle handlers
export default function UserDetailsPanel({
  user,
  currentUserId,
  onChanged,
  onLock,
  onUnlock,
  onSurrender
}) {
  const { me } = useAuth();
  const viewerIsMaster = Boolean(me && me.isMaster === true);
  const [editing, setEditing] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  // Stage 5L: controlled tabs + focus/scroll target so the action
  // buttons (Reset password / Change password / Edit profile) can
  // route the user straight to the relevant section even when they're
  // currently on a different tab. focusedSection drives a transient
  // ring highlight that auto-clears after ~1.8s.
  const [activeTab, setActiveTab] = useState('profile');
  const [focusedSection, setFocusedSection] = useState(null);

  // Reset transient state whenever a new user is selected.
  useEffect(() => {
    setEditing(false);
    setResetOpen(false);
    setChangeOpen(false);
    setActiveTab('profile');
    setFocusedSection(null);
  }, [user && user.id]);

  // Auto-clear the highlight after a short flash so it doesn't stay
  // visible forever once the user starts typing.
  useEffect(() => {
    if (!focusedSection) return undefined;
    const t = setTimeout(() => setFocusedSection(null), 1800);
    return () => clearTimeout(t);
  }, [focusedSection]);

  // Action-button jump handlers: switch to the right tab, open/show
  // the right form, and arm the highlight. The panels themselves
  // handle scroll-into-view + first-input focus on mount via a
  // useEffect (see EditProfileForm / AdminResetPasswordPanel /
  // ChangeMyPasswordPanel below).
  function jumpToEditProfile() {
    setActiveTab('profile');
    setEditing(true);
    setFocusedSection('profile');
  }
  function jumpToResetPassword() {
    setActiveTab('security');
    setChangeOpen(false);
    setResetOpen(true);
    setFocusedSection('reset');
  }
  function jumpToChangePassword() {
    setActiveTab('security');
    setResetOpen(false);
    setChangeOpen(true);
    setFocusedSection('change');
  }

  const selfMode = Boolean(currentUserId && user && currentUserId === user.id);

  // Stage 5L: activity tab always shows the last 10 entries — no
  // pagination. Backend already accepts a `limit` query param via
  // /api/users/:id/activity and /api/users/me/activity.
  const activityQ = useQuery(
    () => {
      if (!user) return Promise.resolve([]);
      return selfMode
        ? endpoints.users.myActivity({ limit: 10 })
        : endpoints.users.activity(user.id, { limit: 10 });
    },
    [user && user.id, selfMode]
  );

  if (!user) {
    return (
      <Card>
        <EmptyState
          icon={<UserOutlined />}
          title="Select a user"
          description="Select a user to view profile, security, and access settings."
        />
      </Card>
    );
  }

  const status = resolveStatus(user);
  const isMaster = user.isMaster === true;
  const isSelf = currentUserId && currentUserId === user.id;
  const initials = initialsOf(user.displayName, user.email);

  function handleSavedProfile(updated) {
    setEditing(false);
    if (typeof onChanged === 'function') onChanged(updated);
  }
  function handleResetDone() {
    setResetOpen(false);
    if (typeof onChanged === 'function') onChanged();
  }
  function handleChangeDone() {
    setChangeOpen(false);
    if (typeof onChanged === 'function') onChanged();
  }

  return (
    <Card styles={{ body: { padding: 0 } }} className="overflow-hidden">
      {/* ───────────────────────── Header ─────────────────────────
          Two-column layout: identity (avatar + name + tags) on the
          left, primary actions on the right. Both columns share the
          same vertical center line so the eye lands cleanly across
          the modal. Banners (surrender / master-protection) sit
          below the row so they don't crowd the identity block. */}
      <header className="px-6 pt-6 pb-5 border-b border-surface-200 bg-gradient-to-br from-brand-50/70 via-brand-50/20 to-white">
        <div className="flex items-start justify-between gap-5 flex-wrap">
          {/* Identity column */}
          <div className="flex items-start gap-3.5 min-w-0 flex-1">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-600 text-white text-sm font-bold shadow-sm ring-2 ring-white shrink-0">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-ink-900 m-0 truncate leading-tight tracking-tight">
                {user.displayName || user.fullName || user.email}
              </h2>
              <p className="text-sm text-ink-500 m-0 mt-0.5 truncate">{user.email}</p>
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                <StatusBadge status={isMaster ? 'master' : user.role || 'viewer'} />
                <StatusBadge status={status} />
                {isSelf ? (
                  <Tag color="blue" className="!m-0 !leading-5">You</Tag>
                ) : null}
              </div>
            </div>
          </div>

          {/* Action column — only the buttons; banners drop below. */}
          {status === 'surrendered' || (isMaster && !isSelf) ? null : (
            <div className="flex items-center gap-2 shrink-0">
              {isSelf ? (
                <>
                  <Button
                    icon={<EditOutlined />}
                    onClick={jumpToEditProfile}
                    disabled={editing && activeTab === 'profile'}
                    className="!h-9 !rounded-lg !font-medium"
                  >
                    Edit profile
                  </Button>
                  <Button
                    type="primary"
                    icon={<KeyOutlined />}
                    onClick={jumpToChangePassword}
                    disabled={changeOpen && activeTab === 'security'}
                    className="!h-9 !rounded-lg !font-medium !bg-[#1877F2] hover:!bg-blue-700 !border-0 !shadow-sm"
                  >
                    Change password
                  </Button>
                </>
              ) : (
                <>
                  {status === 'active' ? (
                    <Button
                      icon={<LockOutlined className="!text-white" />}
                      className="!h-9 !rounded-lg !border-0 !bg-amber-500 !px-3.5 !font-semibold !text-white shadow-sm hover:!bg-amber-600"
                      onClick={() => {
                        if (typeof onLock === 'function') onLock(user);
                      }}
                    >
                      Lock
                    </Button>
                  ) : null}
                  {status === 'locked' ? (
                    <Button
                      icon={<UnlockOutlined className="!text-white" />}
                      className="!h-9 !rounded-lg !border-0 !bg-amber-500 !px-3.5 !font-semibold !text-white shadow-sm hover:!bg-amber-600"
                      onClick={() => {
                        if (typeof onUnlock === 'function') onUnlock(user);
                      }}
                    >
                      Unlock
                    </Button>
                  ) : null}
                  <Button
                    icon={<KeyOutlined className="!text-white" />}
                    className="!h-9 !rounded-lg !border-0 !bg-[#1877F2] !px-3.5 !font-semibold !text-white shadow-sm hover:!bg-blue-700"
                    onClick={jumpToResetPassword}
                    disabled={resetOpen && activeTab === 'security'}
                  >
                    Reset password
                  </Button>
                  <Button
                    icon={<StopOutlined className="!text-white" />}
                    className="!h-9 !rounded-lg !border-0 !bg-red-500 !px-3.5 !font-semibold !text-white shadow-sm hover:!bg-red-600"
                    onClick={() => {
                      if (typeof onSurrender === 'function') onSurrender(user);
                    }}
                  >
                    Surrender
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* State banners — full-width below the identity/action row. */}
        {status === 'surrendered' ? (
          <p className="text-xs text-rose-700 bg-rose-50/70 border border-rose-200 rounded-lg px-3 py-2 m-0 mt-4 flex items-center gap-2">
            <StopOutlined className="text-rose-500" aria-hidden="true" />
            This user has been surrendered and cannot log in.
          </p>
        ) : isMaster && !isSelf ? (
          <p className="text-xs text-amber-700 bg-amber-50/70 border border-amber-200 rounded-lg px-3 py-2 m-0 mt-4 flex items-center gap-2">
            <SafetyCertificateOutlined className="text-amber-500" aria-hidden="true" />
            Master account is protected and cannot be locked or surrendered.
          </p>
        ) : null}
      </header>

      <Tabs
        activeKey={activeTab}
        onChange={(k) => setActiveTab(k)}
        size="small"
        className="gmail-user-tabs !px-6 !pt-3"
        items={[
          {
            key: 'profile',
            label: (
              <span className="flex items-center gap-1.5">
                <IdcardOutlined /> Profile
              </span>
            ),
            children: (
              <div className="pb-5">
                {editing ? (
                  <EditProfileForm
                    user={user}
                    selfMode={selfMode}
                    onSaved={handleSavedProfile}
                    onCancel={() => setEditing(false)}
                    highlight={focusedSection === 'profile'}
                  />
                ) : (
                  <>
                    <h3 className="text-[11px] uppercase tracking-[0.14em] text-ink-500 font-semibold m-0 mb-2">
                      Contact profile
                    </h3>
                    <dl className="rounded-xl border border-surface-200 divide-y divide-surface-200 mb-4 bg-white overflow-hidden">
                      <div className="flex items-center justify-between px-3.5 py-2.5 text-sm gap-3 hover:bg-surface-50/60 transition-colors">
                        <dt className="text-ink-500">Display name</dt>
                        <dd className="text-ink-900 font-medium text-right truncate">
                          {user.displayName || '—'}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between px-3.5 py-2.5 text-sm gap-3 hover:bg-surface-50/60 transition-colors">
                        <dt className="text-ink-500">Mobile (contact)</dt>
                        <dd className="text-ink-900 font-medium text-right truncate">
                          {user.profileMobile || '—'}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between px-3.5 py-2.5 text-sm gap-3 hover:bg-surface-50/60 transition-colors">
                        <dt className="text-ink-500">Email (contact)</dt>
                        <dd className="text-ink-900 font-medium text-right truncate">
                          {user.profileEmail || '—'}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between px-3.5 py-2.5 text-sm gap-3 hover:bg-surface-50/60 transition-colors">
                        <dt className="text-ink-500">Username</dt>
                        <dd>
                          <code className="text-xs text-ink-700 bg-surface-100 px-1.5 py-0.5 rounded">{user.username}</code>
                        </dd>
                      </div>
                      <div className="flex items-center justify-between px-3.5 py-2.5 text-sm gap-3 hover:bg-surface-50/60 transition-colors">
                        <dt className="text-ink-500">Login email</dt>
                        <dd className="text-ink-900 font-medium text-right truncate">
                          {user.email}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between px-3.5 py-2.5 text-sm gap-3 hover:bg-surface-50/60 transition-colors">
                        <dt className="text-ink-500">Created</dt>
                        <dd className="text-ink-900 font-medium text-right">
                          {formatDateTime(user.createdAt)}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between px-3.5 py-2.5 text-sm gap-3 hover:bg-surface-50/60 transition-colors">
                        <dt className="text-ink-500">Last login</dt>
                        <dd className="text-ink-900 font-medium text-right">
                          {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : '—'}
                        </dd>
                      </div>
                    </dl>
                    <Button
                      type="default"
                      icon={<EditOutlined />}
                      onClick={jumpToEditProfile}
                      className="!h-9 !rounded-lg !font-medium"
                    >
                      Edit profile
                    </Button>
                  </>
                )}
              </div>
            )
          },
          {
            key: 'security',
            label: (
              <span className="flex items-center gap-1.5">
                <SafetyCertificateOutlined /> Access &amp; security
              </span>
            ),
            children: (
              <div className="pb-5">
                <h3 className="text-[11px] uppercase tracking-[0.14em] text-ink-500 font-semibold m-0 mb-2">
                  Account state
                </h3>
                <dl className="rounded-xl border border-surface-200 divide-y divide-surface-200 mb-4 bg-white overflow-hidden">
                  <div className="flex items-center justify-between px-3.5 py-2.5 text-sm gap-3 hover:bg-surface-50/60 transition-colors">
                    <dt className="text-ink-500">Role</dt>
                    <dd>
                      <StatusBadge status={isMaster ? 'master' : user.role || 'viewer'} />
                    </dd>
                  </div>
                  <div className="flex items-center justify-between px-3.5 py-2.5 text-sm gap-3 hover:bg-surface-50/60 transition-colors">
                    <dt className="text-ink-500">Status</dt>
                    <dd>
                      <StatusBadge status={status} />
                    </dd>
                  </div>
                  <div className="flex items-center justify-between px-3.5 py-2.5 text-sm gap-3 hover:bg-surface-50/60 transition-colors">
                    <dt className="text-ink-500">Last password changed</dt>
                    <dd className="text-ink-900 font-medium text-right">
                      {user.lastPasswordChangedAt
                        ? formatDateTime(user.lastPasswordChangedAt)
                        : '—'}
                    </dd>
                  </div>
                </dl>

                {/* Conditional mount avoids the AntD "useForm not
                    connected" warning — Form.useForm runs at panel
                    component scope, so the panel must be rendered
                    only when its <Form> will mount. */}
                {resetOpen ? (
                  <AdminResetPasswordPanel
                    user={user}
                    onClose={() => setResetOpen(false)}
                    onDone={handleResetDone}
                    highlight={focusedSection === 'reset'}
                  />
                ) : null}
                {changeOpen ? (
                  <ChangeMyPasswordPanel
                    onClose={() => setChangeOpen(false)}
                    onDone={handleChangeDone}
                    highlight={focusedSection === 'change'}
                  />
                ) : null}

                {viewerIsMaster ? (
                  <UserIpAccessSection user={user} onChanged={onChanged} />
                ) : null}

                {/* Stage 5M: per-sub-user MFA toggle. The master's own
                    TOTP setup (QR code, verify, enable/disable) lives
                    in the dedicated "Authenticator" tab below. Here
                    we only surface the per-user mfa_required toggle
                    when the master is viewing a sub-user. */}
                {viewerIsMaster && !selfMode && !isMaster ? (
                  <UserMfaSection user={user} onChanged={onChanged} />
                ) : null}
              </div>
            )
          },
          // Stage 5M: dedicated "Authenticator" tab — only visible
          // when the master is viewing their own profile. Owns the QR
          // setup, manual key, verify-and-enable flow, and the global
          // enable/disable switch. Pulled out of the Access & security
          // tab so its dense setup state has its own canvas.
          ...(viewerIsMaster && selfMode && isMaster
            ? [{
                key: 'mfa',
                label: (
                  <span className="flex items-center gap-1.5">
                    <QrcodeOutlined /> Authenticator
                  </span>
                ),
                children: (
                  <div className="pb-5">
                    <MasterMfaSection onChanged={onChanged} />
                    {/* Stage 5M-Recovery — recovery codes section.
                        Self-contained; fetches MFA + codes status on
                        mount. Only shown to a master viewing their
                        own profile, same as MasterMfaSection above. */}
                    <RecoveryCodesSection />
                  </div>
                )
              }]
            : []),
          {
            key: 'activity',
            label: (
              <span className="flex items-center gap-1.5">
                <HistoryOutlined /> Activity
              </span>
            ),
            children: (
              <ActivitySection
                loading={activityQ.loading}
                items={activityQ.data || []}
                error={activityQ.error}
                onRefresh={activityQ.refetch}
              />
            )
          }
        ]}
      />
    </Card>
  );
}
